import ThemedSelect from "../components/shared/ThemedSelect.jsx";
import { useEffect, useState } from "react";
import { Alert, Form } from "react-bootstrap";
import { ReportBlock, ReportFigure, ReportMasthead } from "../components/reports/ReportDocument.jsx";
import RulesHelp from "../components/shared/RulesHelp.jsx";
import { getKpiReport, listKpiCompanies } from "../services/apiClient.js";
import { monthKey } from "../utils/date.js";

// The monthly KPI pack GPS sends each assessor company, assembled instead of
// hand-collected out of three screens.
//
// Two deliberate differences from the pack GPS sends today, both of which are the
// point of building it:
//
//   1. every figure shows the population it was measured over, so "93%" is always
//      "93% of what". A number that goes to an insurer under GPS's name should be
//      answerable a year later;
//   2. an empty band says so instead of printing a figure. If no completed job in
//      the period was over $30K, the honest output is "no completed jobs in this
//      band", not a number.
//
// Assumptions are listed at the foot of the page as well as in
// docs/business-rules/kpi-report.md, so whoever reads the pack can see how it was
// built without opening the code.

// "n months ago" as "YYYY-MM", from the NZ calendar (monthKey, src/utils/date.js —
// the hand-rolled version this replaces read the BROWSER's month). The month picker
// is a DROPDOWN rather than a raw month input, which reads as a plain textbox (the
// same call Robert made on the Strike Rate report, 2026-07-17).
const monthAgo = (n) => monthKey(-n);

const MONTH_OPTIONS = Array.from({ length: 36 }, (_, i) => {
  const value = monthAgo(i);
  const [y, mo] = value.split("-");
  return {
    value,
    label: new Date(Number(y), Number(mo) - 1, 1).toLocaleDateString("en-NZ", { month: "short", year: "numeric" }),
  };
});

// The pack itself spells the month out in full: it is a document, not a filter.
const monthLabel = (month) => {
  const [y, mo] = String(month).split("-");
  return new Date(Number(y), Number(mo) - 1, 1).toLocaleDateString("en-NZ", { month: "long", year: "numeric" });
};

const days = (n) => (n == null ? null : `${n.toFixed ? n.toFixed(2) : n} days`);

function LifeBlock({ title, block }) {
  const band = (b) =>
    b.claims === 0
      ? { value: "No completed jobs in this band", working: null, muted: true }
      : { value: days(b.average_days), working: `average over ${b.claims} completed job${b.claims === 1 ? "" : "s"}` };
  const under = band(block.under);
  const over = band(block.over);
  return (
    <ReportBlock title={title}>
      <ReportFigure label="Total claims completed" value={block.completed} />
      <ReportFigure label={block.under.label} value={under.value} working={under.working} muted={under.muted} />
      <ReportFigure label={block.over.label} value={over.value} working={over.working} muted={over.muted} />
      {/* Completed jobs with nothing invoiced yet (so no $ band) are NOT
          printed as a row — "Not banded" meant nothing to anyone reading the
          pack (Robert + KS 2026-09-07). Nothing else changes: the total still
          counts every job completed in the month, and the API still returns
          the unbanded count and reasons for KS. */}
    </ReportBlock>
  );
}


// This was the first report taken off the reports master switch (Robert
// 2026-08-03) — finished, being invoiced for, and Karen Sweatman asking for
// changes to it, which is a report in use rather than a preview of phase 2.
// The switch itself was retired on 2026-08-07 and every report is open now, so
// there is nothing left to be exempt from; the note stays only because it is
// why this one went first.
function KpiReportPage() {
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");   // "" = every company
  const [month, setMonth] = useState(monthAgo(1));  // last completed month
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    listKpiCompanies()
      .then(setCompanies)
      .catch(() => setError("Unable to load the company list."));
  }, []);

  useEffect(() => {
    if (!month) return;
    setLoading(true);
    setError("");
    getKpiReport({ assessor_company_id: companyId, month })
      .then(setReport)
      .catch((err) => {
        setReport(null);
        setError(err.response?.data?.error || "Unable to build this report.");
      })
      .finally(() => setLoading(false));
  }, [companyId, month]);

  // Every company at once: the per-company sections would just repeat the
  // all-insurer ones, so they are left out rather than printed twice.
  const allCompanies = !!report && report.company.id == null;

  return (
    <section className="page-panel">
      {/* App chrome, not part of the document: the pack carries its own GPS
          masthead, so printing "KPI Report" above it just repeats the title and
          costs a page. Other reports keep their heading on paper because there it
          is the only title they have. */}
      <div className="page-heading no-print">
        <h2>KPI Report <RulesHelp topic="kpiReport" /></h2>
      </div>

      {/* Controls and pack share one column, left-aligned like every other page in
          the app, so the two do not drift apart on a wide screen. */}
      <div className="report-column" style={{ maxWidth: "48rem" }}>
        <div className="report-controls no-print d-flex flex-wrap align-items-end gap-2 mb-3">
          <Form.Group>
            <Form.Label className="mb-0 small fw-semibold">Company</Form.Label>
            <ThemedSelect
              size="sm"
              style={{ width: 230 }}
              value={companyId}
              onChange={(e) => setCompanyId(e.target.value)}
            >
              {/* The whole month across everyone: GPS's own view, and the honest
                  denominator behind the "all insurers" lines on each pack. */}
              <option value="">All companies</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </ThemedSelect>
          </Form.Group>
          <Form.Group>
            <Form.Label className="mb-0 small fw-semibold">Month</Form.Label>
            <ThemedSelect
              size="sm"
              style={{ width: 130 }}
              value={month}
              onChange={(e) => setMonth(e.target.value)}
            >
              {MONTH_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </ThemedSelect>
          </Form.Group>
          <div className="text-body-secondary small ms-auto align-self-center">
            Print from the printer button in the header.
          </div>
        </div>

      {error ? <Alert variant="danger">{error}</Alert> : null}
      {loading && !report ? <div className="text-body-secondary">Building the report…</div> : null}

      {report ? (
        <div className="report-doc border rounded">
          <ReportMasthead
            report="KPI Report"
            title={allCompanies ? "All companies" : report.company.name}
            subtitle={`Grayburn Property Services · ${monthLabel(report.period.month)}`}
          />
          <div className="report-body">

          <ReportBlock title={`Service Level Agreement, ${monthLabel(report.period.month)}`}>
            <ReportFigure
              label="Jobs measured, all insurers"
              value={report.sla.all_insurers.jobs_measured}
              working={report.sla.all_insurers.population}
            />
            {report.sla.all_insurers.lines.map((line) => (
              <ReportFigure
                key={line.key}
                label={line.label}
                value={line.percent == null ? "No jobs to measure" : `${line.percent}%`}
                working={line.percent == null ? null : `${line.acceptable} of ${line.n} jobs, target ${line.target}%`}
              />
            ))}
          </ReportBlock>

          {/* The same three lines for this company's own jobs. GPS sends the
              all-insurer figures today; an insurer nearly always wants to know how
              their own work went, so both are here and the choice is GPS's. On the
              all-companies view they would be the same numbers twice. */}
          {allCompanies ? null : (
            <ReportBlock title={`${report.company.name} jobs only`}>
              <ReportFigure label="Jobs measured" value={report.sla.company.jobs_measured} />
              {report.sla.company.lines.map((line) => (
                <ReportFigure
                  key={line.key}
                  label={line.label}
                  value={line.percent == null ? "No jobs to measure" : `${line.percent}%`}
                  working={line.percent == null ? null : `${line.acceptable} of ${line.n} jobs, target ${line.target}%`}
                  muted={line.percent == null}
                />
              ))}
            </ReportBlock>
          )}

          <LifeBlock
            title={`File Life, ${report.company.name}, ${monthLabel(report.period.month)}`}
            block={report.file_life.company_month}
          />
          <LifeBlock
            title={`File Life, ${report.company.name}, year to date ${report.period.year}`}
            block={report.file_life.company_ytd}
          />
          {allCompanies ? null : (
            <LifeBlock
              title={`File Life, all insurers, year to date ${report.period.year}`}
              block={report.file_life.all_insurers_ytd}
            />
          )}

          <ReportBlock title="Positive Feedback">
            <ReportFigure label={`${report.company.name}, ${monthLabel(report.period.month)}`} value={report.feedback.company_month} />
            {allCompanies ? null : (
              <ReportFigure label={`All insurers, ${monthLabel(report.period.month)}`} value={report.feedback.all_month} />
            )}
            <ReportFigure label={`${report.company.name}, year to date`} value={report.feedback.company_ytd} muted />
            {allCompanies ? null : (
              <ReportFigure label="All insurers, year to date" value={report.feedback.all_ytd} muted />
            )}
          </ReportBlock>

            {/* The working stays ON the document, at the foot where a reader expects
                it. It is short, it is quiet, and it is the difference between a
                figure an insurer can query and a figure they have to take on
                trust. */}
            <footer className="report-notes pt-2 mt-4">
              <div className="fw-semibold">How these numbers are worked out</div>
              <ul>
                <li>
                  SLA covers Standard R&amp;Q jobs logged in the month, excluding quick declines
                  and jobs accepted the day they were logged with no inspection booked. That is
                  the same population the SLA report uses.
                </li>
                <li>
                  File Life is business days from Accepted to Completed, less days on hold that
                  were not GPS's fault. Same definition as the File Life report.
                </li>
                <li>
                  The ${(report.file_life.company_month.threshold / 1000).toFixed(0)}K split uses
                  the {report.file_life.company_month.banded_on}. A completed job with nothing
                  invoiced is shown as not banded rather than counted as under.
                </li>
                <li>
                  Feedback counts progress entries marked as positive feedback, on the date they
                  were entered.
                </li>
                <li>Company figures follow the assessor company on the claim.</li>
              </ul>
            </footer>
          </div>
        </div>
      ) : null}

      </div>
    </section>
  );
}

export default KpiReportPage;
